import classNames from 'classnames';
import { useFormikContext } from 'formik';
import { useEffect } from 'react';
import { Flex, FlexGrid, useModal } from 'yoso';
import { ContentSelectFoggyBox } from '~/components/Box/Foggy/Select/ContentSelectFoggyBox';
import { PlaceholderCard } from '~/components/Card/Placeholder';
import { Content } from '~/components/Content';
import { PlaceholderGrid } from '~/components/Grid/Placeholder/PlaceholderGrid';
import { T, t } from '~/components/I18n';
import { useLevelUpRequirement } from '~/components/Level/Level.hooks';
import { LevelUpValues } from '~/components/LevelUp/LevelUp';
import { useCitizensByLevel } from '~/components/Membership/Membership.hooks';
import { Alert } from '~/components/Modal/Alert/Alert';
import { NftCheckboxField } from '~/components/Nft/Checkbox/Field/NftCheckboxField';
import { useTransition } from '~/components/Security';
import styles from './LevelUpSelect.module.scss';

export const LevelUpSelect = () => {
  const { values, errors, setFieldValue, validateForm } = useFormikContext<LevelUpValues>();
  const { level, tokenIdList } = values;
  const { data: citizensByLevel } = useCitizensByLevel(level);
  const {
    data: { minNftAmount },
  } = useLevelUpRequirement(level);
  const { open } = useModal();
  const [proceeding, proceed] = useTransition();

  useEffect(() => {
    if (errors.tokenIdList === 'SELECT_OVER_REQUIRED_NFT' && !proceeding) {
      proceed(async () => {
        setFieldValue('tokenIdList', tokenIdList.slice(0, -1), false);
        await open(
          <Alert
            description={t('citizen_membership_levelup_sec1_popup_desc')}
            confirmText={t('citizen_confirm_ok')}
          />
        );
        await validateForm();
      });
    }
  }, [errors.tokenIdList, open, proceeding, tokenIdList, setFieldValue, validateForm]);

  useEffect(() => {
    requestAnimationFrame(() => validateForm());
  }, [citizensByLevel, validateForm]);

  return (
    <div className={styles.root}>
      <ContentSelectFoggyBox contentLength={citizensByLevel.length}>
        <PlaceholderGrid className={styles.list} placeholder={<PlaceholderCard type="face" />}>
          {citizensByLevel.map((nft) => (
            <FlexGrid className={styles.item} key={nft.id}>
              <NftCheckboxField name="tokenIdList" value={String(nft.id)}>
                <Content
                  {...nft}
                  className={styles.content}
                  contentsType="IMAGE"
                  draggable={false}
                />
              </NftCheckboxField>
            </FlexGrid>
          ))}
        </PlaceholderGrid>
      </ContentSelectFoggyBox>
      <div className={styles.footer}>
        <Flex className={styles.condition} container justifyContent="space-between">
          <span className={styles.conditionTitle}>
            <T name="citizen_membership_levelup_sec1_selected" />
          </span>
          <span className={styles.conditionCount}>
            <strong>{tokenIdList.length}</strong> / {minNftAmount}
          </span>
        </Flex>
        <p
          className={classNames(styles.conditionDescription, {
            [styles.conditionDescriptionActive]: !errors.tokenIdList,
          })}>
          {(() => {
            switch (errors.tokenIdList) {
              case 'NOT_ENOUGH_WALLET_NFTS':
                return (
                  <T
                    name="citizen_membership_levelup_sec1_desc2"
                    options={[minNftAmount - citizensByLevel.length]}
                  />
                );
              case 'NOT_ENOUGH_SELECTED_NFTS':
                return (
                  <T
                    name="citizen_membership_levelup_sec1_desc3"
                    options={[minNftAmount - tokenIdList.length]}
                  />
                );
              default:
                return <T name="citizen_membership_levelup_sec1_desc4" />;
            }
          })()}
        </p>
      </div>
    </div>
  );
};
